import { OpenFeature } from '@openfeature/web-sdk';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import type { FeatureFlagContext } from '@risksmart-app/feature-flags/src/context';
import { toEvaluationContext } from '@risksmart-app/feature-flags/src/context';
import type { FeatureFlagProviderKind } from '@risksmart-app/feature-flags/src/provider-kind';
import {
  createWebFeatureFlagProvider,
  webProviderConfigSchema,
} from '@risksmart-app/feature-flags/src/web/create-web-provider';
import { toDatadogEnv } from '@risksmart-app/shared/src/utils/datadog-environment';
import * as Sentry from '@sentry/react';

/**
 * Resolves the OpenFeature provider config from REACT_APP_* env vars:
 *
 * - `REACT_APP_FEATURE_FLAG_PROVIDER` — `flagd` | `datadog` | `in-memory`;
 *   defaults to `in-memory` (registry defaults) when unset, so builds without
 *   flagging config never break.
 * - flagd: `REACT_APP_FLAGD_HOST` / `REACT_APP_FLAGD_PORT` (defaults
 *   `localhost:8013`, matching the docker compose sidecar) and
 *   `REACT_APP_FLAGD_TLS` (`'true'` connects over HTTPS — e.g. the ephemeral
 *   bundle reaching flagd through the ALB; defaults off for the plaintext
 *   local sidecar).
 * - datadog: `REACT_APP_DD_CLIENT_TOKEN` (public RUM client token — absence
 *   means "not configured for this environment" and falls back quietly to
 *   in-memory defaults), `REACT_APP_DD_APPLICATION_ID`, `REACT_APP_DD_SITE`
 *   (default `datadoghq.eu`). The Datadog `env` is derived from
 *   `REACT_APP_ENVIRONMENT` via `toDatadogEnv` (app→prod) for parity with the
 *   server's `DD_ENV`.
 *
 * Malformed config throws a ZodError, caught and surfaced by
 * `initialiseWebFeatureFlags`.
 */
const resolveWebProviderConfig = () => {
  const kind = getEnv('REACT_APP_FEATURE_FLAG_PROVIDER', true) || 'in-memory';

  switch (kind) {
    case 'flagd':
      return webProviderConfigSchema.parse({
        kind,
        host: getEnv('REACT_APP_FLAGD_HOST', true) || 'localhost',
        port: getEnv('REACT_APP_FLAGD_PORT', true) || 8013,
        // TLS is env-driven (default off): local `api:v3` reaches the sidecar
        // over plaintext localhost:8013, while the ephemeral bundle reaches
        // flagd through the ALB over HTTPS (REACT_APP_FLAGD_TLS="true").
        tls: getEnv('REACT_APP_FLAGD_TLS', true) === 'true',
      });
    case 'datadog': {
      const clientToken = getEnv('REACT_APP_DD_CLIENT_TOKEN', true);

      // No RUM creds = "not configured for this environment", not a
      // misconfiguration: deployed builds hardwire the datadog provider and
      // opt in per environment by setting the token, so its absence must boot
      // clean on registry defaults rather than error.
      if (!clientToken) {
        return webProviderConfigSchema.parse({ kind: 'in-memory' });
      }

      return webProviderConfigSchema.parse({
        kind,
        clientToken,
        applicationId: getEnv('REACT_APP_DD_APPLICATION_ID', true) || undefined,
        site: getEnv('REACT_APP_DD_SITE', true) || 'datadoghq.eu',
        // app→prod parity with the server's DD_ENV; `||` (not `??`) so an
        // empty REACT_APP_ENVIRONMENT also falls back.
        env: toDatadogEnv(getEnv('REACT_APP_ENVIRONMENT', true)) || 'unknown',
        service: 'risksmart-web',
      });
    }
    default:
      return webProviderConfigSchema.parse({ kind });
  }
};

let activeProviderKind: FeatureFlagProviderKind | null = null;

export const getActiveFeatureFlagProviderKind =
  (): FeatureFlagProviderKind | null => activeProviderKind;

/**
 * Registers the configured OpenFeature provider. Idempotent — called once at
 * boot from `index.tsx`, guarded so stray re-invocations (tests, StrictMode)
 * are no-ops. Non-fatal on misconfiguration: the app starts with no provider
 * registered and every flag evaluates to its default value.
 */
export const initialiseWebFeatureFlags = (): void => {
  if (activeProviderKind !== null) {
    return;
  }

  try {
    const config = resolveWebProviderConfig();
    OpenFeature.setProvider(createWebFeatureFlagProvider(config));
    activeProviderKind = config.kind;
  } catch (error) {
    // The SDK serves registry defaults with no provider registered; report to
    // Sentry so a misconfigured build surfaces in observability.
    Sentry.captureException(error, { tags: { component: 'feature-flags' } });
  }
};

/**
 * Binds the signed-in user's organisation onto the global evaluation context
 * (zod-validated; orgId becomes the targeting key). Providers re-evaluate
 * flags against the new context automatically.
 */
export const setFeatureFlagContext = async (
  context: FeatureFlagContext
): Promise<void> => {
  await OpenFeature.setContext(toEvaluationContext(context));
};

/**
 * Clears the global evaluation context (logout / provider unmount) so a
 * signed-out session never evaluates flags against a stale identity.
 */
export const clearFeatureFlagContext = async (): Promise<void> => {
  await OpenFeature.clearContext();
};
